import { useState, useEffect, useMemo } from "react";
import styles from "../styles/pages/Admin.module.css";

import { AccountCardRow, ACCOUNT_BODY_COLUMNS } from "../ui/cards/AdminCards";
import SearchInput from "../ui/inputs/SearchInput";
import ChoiceChip from "../ui/selectors/ChoiceChip";
import Button from "../ui/buttons/Button";
import {
  AccountDetailModal,
  CreateAccountModal,
} from "../components/admin/AdminDetailModals";
import MailAccountsAdminTab from "../components/admin/MailAccountsAdminTab";
import MailAccountsToolbar, { MailAccountFilterChips } from "../components/admin/MailAccountsToolbar";
import useMailAccountsAdmin from "../components/admin/useMailAccountsAdmin";

import { ReactComponent as Vector9Icon } from "../assets/images/vector_arrow.svg";
import { ReactComponent as AddIcon } from "../assets/icons/add.svg";

import { useAuth } from "../contexts/AuthContext";

import LoadingScreen from "../ui/loadingScreen/LoadingScreen";

function HeaderCell({ children, column, align = "left" }) {
  return (
    <div
      style={{
        gridColumn: column,
        textAlign: align,
        color: "var(--color-text-text-primary)",
        fontSize: 14,
        fontFamily: "DM Sans",
        fontWeight: 500,
        lineHeight: "17px",
        minWidth: 0,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </div>
  );
}

const ADMIN_TABS = [
  { id: "cuentas", label: "Cuentas" },
  { id: "casillas", label: "Casillas" },
];

function buildSupervisor(account) {
  const admin = account.admins?.[0];
  if (!admin) return { name: "-", photo: null };
  return {
    name: admin.name || admin.username || "-",
    photo: admin.photo ?? admin.avatar_url ?? null,
  };
}

function getPill(account) {
  return account.name ?? "-";
}

function AccountsTable({ rows, onSelect }) {
  if (!rows.length) {
    return <div className={styles.emptyState}>No hay cuentas para mostrar</div>;
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.table}>
        <div className={styles.headerContainer}>
          <div className={styles.headerBody}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: ACCOUNT_BODY_COLUMNS,
                alignItems: "center",
                minWidth: 0,
              }}
            >
              <HeaderCell column="1">Pill</HeaderCell>
              <HeaderCell column="2">Nombre</HeaderCell>
              <HeaderCell column="3">Supervisor</HeaderCell>
            </div>
          </div>
          <div className={styles.headerActionSpacer} />
        </div>

        {rows.map((account) => {
          const sup = buildSupervisor(account);
          return (
            <div
              key={`account-${account.id}`}
              className={styles.rowWrapper}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(account)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSelect(account);
                if (e.key === " ") {
                  e.preventDefault();
                  onSelect(account);
                }
              }}
            >
              <AccountCardRow
                pill={getPill(account)}
                name={account.name ?? "-"}
                supervisorName={sup.name}
                supervisorPhoto={sup.photo}
                actionIcon={<Vector9Icon />}
                onAction={() => onSelect(account)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

const AdminPage = () => {
  const {
    supervisedAccounts,
    loading: authLoading,
    addAccount,
    updateAccount,
    removeAccount,
  } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [tab, setTab] = useState("cuentas");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const mailAccountsAdmin = useMailAccountsAdmin({ enabled: tab === "casillas" });

  useEffect(() => {
    setAccounts(supervisedAccounts || []);
    setLoading(authLoading);
  }, [supervisedAccounts, authLoading]);

  const filteredAccounts = useMemo(() => {
    if (!search.trim()) return accounts;
    const lower = search.toLowerCase();
    return accounts.filter(
      (a) =>
        a.name?.toLowerCase().includes(lower) ||
        getPill(a).toLowerCase().includes(lower),
    );
  }, [accounts, search]);

  if (loading) {
    return (
      <LoadingScreen
        mainText="Cargando cuentas..."
        subText="Por favor espera mientras cargamos los datos de las cuentas"
      />
    );
  }

  return (
    <div className={styles.admin}>
      <div className={styles.admin1}>
        <div className={styles.adminInner}>
          <div className={styles.frameParent}>
            <div className={styles.pageTitle}>Cuentas</div>

            <div className={styles.toolbar}>
              <div className={styles.toolbarLeft}>
                {tab === "cuentas" ? (
                  <SearchInput onChange={setSearch} width={241} height={40} />
                ) : (
                  <MailAccountsToolbar
                    mailAccounts={mailAccountsAdmin.mailAccounts ?? []}
                    accounts={accounts}
                    filters={mailAccountsAdmin.filters}
                    onFilterChange={mailAccountsAdmin.setFilters}
                    onOpenMailAccount={mailAccountsAdmin.openMailAccount}
                  />
                )}

                <div className={styles.filterGroup}>
                  {ADMIN_TABS.map((adminTab) => (
                    <ChoiceChip
                      key={adminTab.id}
                      label={adminTab.label}
                      selected={tab === adminTab.id}
                      onClick={() => setTab(adminTab.id)}
                      height={40}
                      iconSize={0}
                      fontWeight={500}
                      selectedColor="var(--text-cta-primary)"
                      unselectedColor="var(--text-tertiary)"
                      selectedBackground="var(--surface-accent-hover)"
                      unselectedBackground="transparent"
                    />
                  ))}
                </div>

                {tab === "casillas" && (
                  <MailAccountFilterChips
                    mailAccounts={mailAccountsAdmin.mailAccounts ?? []}
                    accounts={accounts}
                    filters={mailAccountsAdmin.filters}
                    onFilterChange={mailAccountsAdmin.setFilters}
                  />
                )}
              </div>

              {(tab === "cuentas" || mailAccountsAdmin.mailAccounts !== null) && (
                <Button
                  type="button"
                  iconLeft={<AddIcon />}
                  width={148}
                  height={40}
                  onClick={() => (tab === "casillas" ? mailAccountsAdmin.openCreate() : setShowCreate(true))}
                  style={{
                    paddingTop: 10,
                    paddingBottom: 10,
                    paddingLeft: 8,
                    paddingRight: 16,
                    background: "var(--surface-accent-hover)",
                    color: "var(--text-cta-primary)",
                    fontSize: 13,
                    fontFamily: "DM Sans",
                    fontWeight: 500,
                    gap: 0,
                  }}
                >
                  {tab === "casillas" ? "Nueva Casilla" : "Nueva Cuenta"}
                </Button>
              )}
            </div>

            {tab === "cuentas" ? (
              <AccountsTable
                rows={filteredAccounts}
                onSelect={setSelectedAccount}
              />
            ) : (
              <MailAccountsAdminTab accounts={accounts} {...mailAccountsAdmin} />
            )}
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateAccountModal
          onClose={() => setShowCreate(false)}
          onCreated={(newAccount) => {
            setAccounts((prev) => [...prev, newAccount]);
            addAccount?.(newAccount);
            setShowCreate(false);
          }}
        />
      )}

      <AccountDetailModal
        account={selectedAccount}
        accounts={accounts}
        onClose={() => setSelectedAccount(null)}
        onSaved={(updated) => {
          setAccounts((prev) =>
            prev.map((a) => (a.id === updated.id ? updated : a)),
          );
          updateAccount?.(updated);
          setSelectedAccount(updated);
        }}
        onDeleted={(id) => {
          setAccounts((prev) => prev.filter((a) => a.id !== id));
          removeAccount?.(id);
          setSelectedAccount(null);
        }}
      />
    </div>
  );
};

export default AdminPage;
